import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Helmet } from "react-helmet-async";
import { Shield, Lock, Zap, Gift, ChevronRight, Eye, EyeOff } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "https://api.sacvpn.com";

// Self-serve "1 year free" redemption. A visitor enters a code, creates an
// account (or applies it to an account they are already signed into), and gets
// a full year of card-free access. No manual account creation by anyone.
export default function Redeem() {
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(params.get("code") || "");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setSignedIn(true);
        setEmail(session.user.email || "");
      }
    })();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setOk("");
    const trimmedCode = code.trim();
    if (!trimmedCode) { setErr("Enter your code."); return; }
    setLoading(true);

    try {
      if (signedIn) {
        // Apply the code to the account already signed in.
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_BASE}/api/promo/redeem`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ code: trimmedCode }),
        });
        const out = await res.json().catch(() => ({}));
        if (!res.ok) { setErr(out.error || "Could not redeem that code."); setLoading(false); return; }
        setOk(out.message || "Your free access is active.");
        setTimeout(() => nav("/app/personal/devices"), 1200);
        return;
      }

      // New account: create it with the promo code so the year is granted on signup.
      if (!email || !password) { setErr("Enter an email and password."); setLoading(false); return; }
      if (password.length < 8) { setErr("Password must be at least 8 characters."); setLoading(false); return; }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { promo_code: trimmedCode } },
      });

      if (error) {
        setErr(error.message || "Sign up failed.");
        setLoading(false);
        return;
      }

      if (data?.promo?.applied) {
        setOk(`You are in. ${data.promo.months} months of SACVPN, on the house.`);
      } else {
        // Account was created but the code did not apply (invalid/used/expired).
        // They still have the standard 14-day trial, so send them in with a note.
        setErr("Your account was created, but that code could not be applied. You are on a 14-day trial.");
        setTimeout(() => nav("/app/personal/devices"), 1800);
        setLoading(false);
        return;
      }
      setTimeout(() => nav("/app/personal/devices"), 1400);
    } catch (e) {
      setErr(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <>
      <Helmet>
        <title>Redeem Your Free Year - SACVPN</title>
        <meta name="description" content="Redeem your SACVPN code for a full year of secure, private WireGuard VPN access. No credit card required." />
        <link rel="canonical" href="https://www.sacvpn.com/redeem" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-brand-950 flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-cyan/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <img src="/sacvpn-logo.svg" alt="SACVPN" className="h-12 w-auto" />
              <span className="text-3xl font-display font-bold text-white">SACVPN</span>
            </Link>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-500/15 border border-brand-500/30">
              <Gift className="w-4 h-4 text-brand-400" />
              <span className="text-sm font-medium text-brand-300">1 year free, no card required</span>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 shadow-2xl">
            <div className="flex justify-center gap-4 mb-6">
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Shield className="w-4 h-4 text-brand-400" /><span>No-Logs</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Lock className="w-4 h-4 text-green-400" /><span>Encrypted</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Zap className="w-4 h-4 text-accent-cyan" /><span>WireGuard</span>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-2">
                  Redemption code
                </label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="SAC-XXXX-XXXX"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all tracking-widest font-mono"
                  required
                />
              </div>

              {!signedIn && (
                <>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                      Choose a Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="w-full px-4 py-3 pr-12 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {signedIn && (
                <p className="text-sm text-gray-400">
                  Signed in as <span className="text-white">{email}</span>. Your code will be applied to this account.
                </p>
              )}

              {err && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-400 text-sm">{err}</p>
                </div>
              )}
              {ok && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <p className="text-green-400 text-sm">{ok}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 text-white font-semibold shadow-lg shadow-brand-500/25 hover:shadow-xl hover:shadow-brand-500/30 transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {loading ? "Working..." : (<>Redeem free year <ChevronRight className="w-5 h-5" /></>)}
              </button>
            </form>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-400">
              Already have an account?{" "}
              <Link to="/login" className="text-brand-400 hover:text-brand-300 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Full access for 12 months. No credit card. Add a payment method later only if you choose to stay.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
